import {
  asValueFrom,
  combineLatestTyped,
  filterNotUndefined,
  getGroupedSetDiff,
  Hue,
  IList,
  ISet,
  isInRange,
  mapToConst,
  mergeTyped,
  promiseState,
  tuple,
  useBooleanState,
  useDataStream,
  useStateAsStream,
  useStreamEffect,
  useStreamValue,
  useVoidEventAsStream
} from 'annotation-tools/utils';
import React, { FC, memo, useCallback } from 'react';
import {
  filter,
  map,
  mapTo,
  scan,
  switchMap,
  withLatestFrom
} from 'rxjs/operators';
import {
  createModelLabel,
  deleteModelLabel,
  updateModelLabel
} from '../../../api/fetch-functions/label';
import { maxStringLength } from '../../../constants/max-string-length';
import { viewTexts } from '../../../constants/view-texts';
import { labelListReducer } from '../../../functions/label-list-reducer';
import { token$ } from '../../../global-state/auth-state';
import {
  ListWithPromiseState,
  listWithPromiseStateInit
} from '../../../global-state/database/common/list-with-status';
import {
  fetchLabelListOf,
  labelListOf
} from '../../../global-state/database/model-label-list';
import { fetchModelListOf } from '../../../global-state/database/model-list';
import { companyId$, modelId$ } from '../../../global-state/router-id';
import { useLoadingPage } from '../../../global-state/show-loading-page';
import { ILabelFromApiType } from '../../../types/record/label';
import { NotFoundTemplate } from '../../shared/not-found-page-template';
import { LabelsView } from './labels-view';

export const Labels: FC<{}> = memo(() => {
  const [editClick$, editOnClick] = useVoidEventAsStream();
  const [cancelClick$, cancelOnClick] = useVoidEventAsStream();
  const [saveClick$, saveOnClick] = useVoidEventAsStream();
  const [addLabelClick$, addLabelOnClick] = useVoidEventAsStream();

  const [
    // dummy comment for prettier to ignore
    deleteLabel$,
    deleteLabel
  ] = useStateAsStream<number | undefined>(undefined);

  const [
    // dummy comment for prettier to ignore
    labelNameChange$,
    labelNameChange
  ] = useStateAsStream<[number, string] | undefined>(undefined);

  const [
    // dummy comment for prettier to ignore
    labelColorChange$,
    labelColorChange
  ] = useStateAsStream<[number, Hue] | undefined>(undefined);

  const labelNameOnChange = useCallback(
    (id: number, name: string) => {
      labelNameChange(tuple(id, name));
    },
    [labelNameChange]
  );

  const labelColorOnChange = useCallback(
    (id: number, hue: Hue) => {
      labelColorChange(tuple(id, hue));
    },
    [labelColorChange]
  );

  const [showErrorString, showError, hideError] = useBooleanState(false);

  /* streams */

  const mode$ = useDataStream<'edit' | 'view'>(
    'view',
    mergeTyped(
      editClick$.pipe(mapToConst('edit')),
      cancelClick$.pipe(mapToConst('view')),
      saveClick$.pipe(mapToConst('view'))
    )
  );

  const labelListFromServer$ = useDataStream<
    ListWithPromiseState<ILabelFromApiType>
  >(
    listWithPromiseStateInit<ILabelFromApiType>(),
    modelId$.pipe(switchMap(labelListOf))
  );

  const labelListBeingEdited$ = useDataStream<
    ListWithPromiseState<ILabelFromApiType>
  >(
    listWithPromiseStateInit<ILabelFromApiType>(),
    mergeTyped(
      editClick$.pipe(
        asValueFrom(labelListFromServer$),
        map(e => ({ type: 'init' as const, listWithPromiseStatus: e }))
      ),
      labelNameChange$.pipe(
        filterNotUndefined(),
        map(e => ({ type: 'change' as const, value: e }))
      ),
      labelColorChange$.pipe(
        filterNotUndefined(),
        map(e => ({ type: 'color' as const, value: e }))
      ),
      addLabelClick$.pipe(mapTo({ type: 'add' as const })),
      deleteLabel$.pipe(
        filterNotUndefined(),
        map(id => ({ type: 'delete' as const, id }))
      )
    ).pipe(
      scan(labelListReducer, listWithPromiseStateInit<ILabelFromApiType>())
    )
  );

  const labelListEditedDiff$ = useDataStream<
    | undefined
    | {
        deleted: IList<ILabelFromApiType>;
        edited: IList<ILabelFromApiType>;
        added: IList<ILabelFromApiType>;
      }
  >(
    undefined,
    saveClick$.pipe(
      asValueFrom(
        combineLatestTyped(labelListFromServer$, labelListBeingEdited$)
      ),
      map(([fromServer, edited]) =>
        fromServer.status === promiseState.error ||
        edited.status === promiseState.error
          ? undefined
          : tuple(fromServer.value.latest, edited.value.latest)
      ),
      filterNotUndefined(),
      map(([labelsFromServer, labelsEdited]) => {
        const diff = getGroupedSetDiff(
          labelsFromServer.toSet(),
          labelsEdited.toSet(),
          e => e.id
        );
        return {
          deleted: diff.get('deleted', ISet<ILabelFromApiType>()).toList(),
          edited: diff.get('updated', ISet<ILabelFromApiType>()).toList(),
          added: diff
            .get('added', ISet<ILabelFromApiType>())
            .toList()
            .sort((a, b) => b.id - a.id)
        };
      })
    )
  );

  const labelNamesAreValid$ = useDataStream<boolean>(
    true,
    labelListBeingEdited$.pipe(
      map(list =>
        list.value.latest.every(e =>
          isInRange(1, maxStringLength.labelName)(e.name.length)
        )
      )
    )
  );

  const labelListForView$ = useDataStream<
    ListWithPromiseState<ILabelFromApiType>
  >(
    listWithPromiseStateInit<ILabelFromApiType>(),
    mode$.pipe(
      switchMap(md =>
        md === 'view' ? labelListFromServer$ : labelListBeingEdited$
      )
    )
  );

  /* extract values */

  const labelList = useStreamValue(labelListForView$);
  const mode = useStreamValue(mode$, 'view');
  const labelNamesAreValid = useStreamValue(labelNamesAreValid$, true);

  useStreamEffect(mode$.pipe(filter(e => e === 'edit')), hideError);

  useStreamEffect(
    labelListEditedDiff$.pipe(
      filterNotUndefined(),
      withLatestFrom(token$.pipe(filterNotUndefined()), modelId$, companyId$)
    ),
    ([diff, token, modelId, companyId]) => {
      /* call update API for edited */
      const promiseForEdited = Promise.all(
        diff.edited.map(e =>
          updateModelLabel(token)(modelId, e.id)({
            hue: e.hue,
            name: e.name
          })
        )
      );

      /* call create API for added in order */
      const promiseForAdded = (async () => {
        const ress = [];
        for (const e of diff.added.values()) {
          ress.push(
            await createModelLabel(token)(modelId)({
              hue: e.hue,
              name: e.name
            })
          );
        }
        return ress;
      })();

      /* call delete API for edited */
      const promiseForDeleted = Promise.all(
        diff.deleted.map(e => deleteModelLabel(token)(modelId, e.id))
      );

      Promise.all([promiseForEdited, promiseForDeleted, promiseForAdded]).then(
        ([resEdited, resDeleted, resAdded]) => {
          if (resEdited.length + resDeleted.length + resAdded.length === 0) {
            return;
          }
          const errors = [...resEdited, ...resDeleted, ...resAdded]
            .filter(r => r.type === 'error')
            .map(r => r.body);

          if (errors.length === 0) {
            hideError();
            fetchLabelListOf(modelId);
            fetchModelListOf(companyId);
          } else {
            showError();
          }
        }
      );
    }
  );

  useLoadingPage(labelList?.status === 'pending');

  return (
    <>
      {labelList === undefined ? (
        undefined
      ) : labelList.status === promiseState.error ? (
        <NotFoundTemplate message={viewTexts.notFoundMessages.labelList} />
      ) : (
        <LabelsView
          mode={mode}
          labelList={labelList.value.latest}
          editOnClick={editOnClick}
          cancelOnClick={cancelOnClick}
          addLabelOnClick={addLabelOnClick}
          saveOnClick={saveOnClick}
          labelNameOnChange={labelNameOnChange}
          labelColorOnChange={labelColorOnChange}
          deleteLabel={deleteLabel}
          showErrorString={showErrorString}
          labelNamesAreValid={labelNamesAreValid}
        />
      )}
    </>
  );
});

Labels.displayName = 'Labels';
